'use client'

import React, { useState, useEffect } from 'react'
import { useTranslations } from 'next-intl'
import { Box, Typography, IconButton, Link as MuiLink, useTheme } from '@mui/material'
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic'
import FlashOnIcon from '@mui/icons-material/FlashOn'
import PaymentIcon from '@mui/icons-material/Payment'
import StarIcon from '@mui/icons-material/Star'
import { SvgIconComponent } from '@mui/icons-material'
import { TwitterIcon } from '@/src/components/common/icons/TwitterIcon'
import { ThreadsIcon } from '@/src/components/common/icons/ThreadsIcon'
import isGlobalDomain from '@/src/utils/generalUtils/isGlobalDomain'

import TopDestinationsWrapper from '@/src/components/common/home/topDestinations'
import ExploreOtherLinksWrapper from '@/src/components/common/home/exploreOtherLinks'
import TopAirlinesWrapper from '@/src/components/common/home/topAirlines'
import HomeHighlights from '@/src/components/common/home/HomeHighlights'
import { isFlyin } from '@/src/utils/generalUtils/isFlyin'
import { CopyRight } from './CopyRight'
import FeatureRenderer from '../../common/featureRenderer'
import { getBrandName } from '@/src/utils/buildUtils'
import { APP_RATINGS, CLEARTRIP_APP_LINKS, FLYIN_APP_LINKS } from '@/src/constants/appDownloadLinks'
import TopCountriesWrapper from '../../common/home/topCountries'
import { Airline } from '@/src/api/requests/topAirlines'
import { getSupportCardsImageSrc } from '@/src/utils/supportCards/getSupportCardsImageSrc'

// Reusable Wave Component
const WaveDivider: React.FC<{ inverted?: boolean; color?: string }> = ({ inverted = false, color }) => {
    const theme = useTheme()
    const waveColor = color ?? theme.palette.primary.main

    if (inverted) {
        // Top wave (inverted/up)
        return (
            <svg
                width="100%"
                height="50%"
                viewBox="0 0 360 19"
                fill="none"
                preserveAspectRatio="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M345.418 0C353.471 0.000103128 360 5.16353 360 14C360 19.5 354.383 18 346.329 18C341.734 18 337.34 18.648 334.412 18H322.871C320.445 18.4185 316.867 18 312.607 18C308.013 18 303.618 18.648 300.69 18H289.149C286.724 18.4184 283.145 18 278.886 18C274.291 18 269.896 18.6481 266.969 18H258.162C255.736 18.4184 252.158 18 247.898 18C243.304 18 238.909 18.6481 235.981 18H225.353C222.927 18.4186 219.349 18 215.089 18C210.494 18 206.1 18.6479 203.172 18H191.631C189.205 18.4185 185.627 18 181.367 18C176.772 18 172.378 18.6479 169.45 18H158.82C156.394 18.4184 152.816 18 148.557 18C143.962 18 139.567 18.6481 136.64 18H125.099C122.673 18.4184 119.094 18 114.835 18C110.24 18 105.846 18.6481 102.918 18H92.2891C89.8632 18.4185 86.2852 18 82.0254 18C77.4306 18 73.0361 18.6479 70.1084 18H60.3896C57.9638 18.4183 54.3862 18 50.127 18C45.5318 18 41.1366 18.6482 38.209 18H25.7568C23.331 18.4184 19.7534 18 15.4941 18C7.44057 18 0 20 0 13.5C0 4.66353 6.52857 9.53566e-05 14.582 0C19.034 0 23.0196 1.57847 25.6943 4.48242C27.5769 5.45527 29.6796 5.99995 31.8984 6C33.9321 6 35.8675 5.54112 37.626 4.71582C40.2907 1.605 44.4904 7.84021e-05 49.2148 0C54.1755 0 58.5572 1.95955 61.1914 5.5293C62.3181 5.83564 63.496 6 64.709 6C66.2323 5.99998 67.7008 5.7431 69.0811 5.26758C71.7088 1.79511 76.1181 0 81.1143 0C85.8903 8.43652e-05 90.1293 1.81662 92.7891 5.1377C94.2723 5.69561 95.8629 5.99995 97.5186 6C99.0423 6 100.511 5.7424 101.892 5.2666C104.519 1.79472 108.928 5.36702e-05 113.924 0C118.7 0 122.94 1.81642 125.6 5.1377C127.083 5.69563 128.673 6 130.329 6C132.362 6 134.298 5.54261 136.056 4.71777C138.72 1.60592 142.92 1.44735e-05 147.646 0C152.606 0 156.987 1.95976 159.621 5.5293C160.748 5.83581 161.926 6 163.14 6C165.173 5.99994 167.108 5.54171 168.866 4.7168C171.531 1.60544 175.731 0 180.456 0C185.416 9.56573e-05 189.797 1.95972 192.432 5.5293C193.558 5.83563 194.736 5.99999 195.949 6C198.868 6 201.586 5.05749 203.866 3.43652C204.17 3.17488 204.487 2.92753 204.817 2.69531C205.083 2.47197 205.343 2.24238 205.594 2H205.921C208.268 0.676171 211.112 6.76747e-05 214.177 0C219.251 0 223.72 2.05089 226.332 5.77832C227.122 5.92357 227.933 6 228.76 6C231.675 5.99994 234.39 5.0596 236.668 3.44238C236.975 3.1782 237.295 2.92955 237.628 2.69531C237.893 2.47197 238.154 2.24239 238.404 2H238.731C241.079 0.676231 243.922 4.904e-06 246.987 0C252.062 0 256.531 2.05077 259.143 5.77832C259.932 5.92345 260.742 5.99998 261.569 6C263.093 6 264.562 5.7424 265.942 5.2666C268.57 1.79474 272.979 1.53018e-05 277.975 0C282.751 0 286.991 1.81637 289.65 5.1377C291.134 5.69564 292.724 6 294.38 6C296.413 5.99998 298.348 5.54236 300.105 4.71777C302.77 1.60564 306.971 0 311.696 0C316.657 2.33496e-05 321.038 1.95974 323.672 5.5293C324.799 5.83563 325.976 5.99996 327.189 6C330.109 6 332.826 5.05746 335.106 3.43652C335.41 3.17546 335.727 2.92903 336.056 2.69727C336.322 2.47339 336.583 2.24302 336.834 2H337.162C339.509 0.676303 342.353 0 345.418 0Z"
                    fill={waveColor}
                />
            </svg>
        )
    }

    // Bottom wave (normal/down)
    return (
        <svg
            width="100%"
            height="50%"
            viewBox="0 0 360 19"
            fill="none"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M345.418 18.29C353.471 18.2899 360 13.1265 360 4.29004C360 -1.20996 354.383 0.290039 346.329 0.290039C341.734 0.290037 337.34 -0.357939 334.412 0.290039H322.871C320.445 -0.128424 316.867 0.290039 312.607 0.290039C308.013 0.290024 303.618 -0.357985 300.69 0.290039H289.149C286.724 -0.12838 283.145 0.290039 278.886 0.290039C274.291 0.290009 269.896 -0.358032 266.969 0.290039H258.162C255.736 -0.128389 252.158 0.290039 247.898 0.290039C243.304 0.29001 238.909 -0.358023 235.981 0.290039H225.353C222.927 -0.128534 219.349 0.290022 215.089 0.290039C210.494 0.290039 206.1 -0.357843 203.172 0.290039H191.631C189.205 -0.128496 185.627 0.290031 181.367 0.290039C176.772 0.290039 172.378 -0.357897 169.45 0.290039H158.82C156.394 -0.12838 152.816 0.290039 148.557 0.290039C143.962 0.290009 139.567 -0.358032 136.64 0.290039H125.099C122.673 -0.128336 119.094 0.290039 114.835 0.290039C110.24 0.289993 105.846 -0.35808 102.918 0.290039H92.2891C89.8632 -0.128489 86.2852 0.290033 82.0254 0.290039C77.4306 0.290039 73.0361 -0.357906 70.1084 0.290039H60.3896C57.9638 -0.12829 54.3862 0.290014 50.127 0.290039C45.5318 0.290039 41.1366 -0.358147 38.209 0.290039H25.7568C23.331 -0.128315 19.7534 0.290009 15.4941 0.290039C7.44057 0.290039 0 -1.70996 0 4.79004C0 13.6265 6.52857 18.2899 14.582 18.29C19.034 18.29 23.0196 16.7116 25.6943 13.8076C27.5769 12.8348 29.6796 12.2901 31.8984 12.29C33.9321 12.29 35.8675 12.7489 37.626 13.5742C40.2907 16.685 44.4904 18.29 49.2148 18.29C54.1755 18.29 58.5572 16.3305 61.1914 12.7607C62.3181 12.4544 63.496 12.29 64.709 12.29C66.2323 12.2901 67.7008 12.5469 69.0811 13.0225C71.7088 16.4949 76.1181 18.29 81.1143 18.29C85.8903 18.29 90.1293 16.4734 92.7891 13.1523C94.2723 12.5944 95.8629 12.2901 97.5186 12.29C99.0423 12.29 100.511 12.5476 101.892 13.0234C104.519 16.4953 108.928 18.29 113.924 18.29C118.7 18.29 122.94 16.4736 125.6 13.1523C127.083 12.5944 128.673 12.29 130.329 12.29C132.362 12.29 134.298 12.7474 136.056 13.5723C138.72 16.6841 142.92 18.29 147.646 18.29C152.606 18.29 156.987 16.3303 159.621 12.7607C160.748 12.4542 161.926 12.29 163.14 12.29C165.173 12.2901 167.108 12.7483 168.866 13.5732C171.531 16.6846 175.731 18.29 180.456 18.29C185.416 18.2899 189.797 16.3303 192.432 12.7607C193.558 12.4544 194.736 12.2901 195.949 12.29C198.868 12.29 201.586 13.2326 203.866 14.8535C204.17 15.1152 204.487 15.3625 204.817 15.5947C205.083 15.8181 205.343 16.0477 205.594 16.29H205.921C208.268 17.6139 211.112 18.29 214.177 18.29C219.251 18.29 223.72 16.2391 226.332 12.5117C227.122 12.3665 227.933 12.29 228.76 12.29C231.675 12.2901 234.39 13.2304 236.668 14.8477C236.975 15.1118 237.295 15.3605 237.628 15.5947C237.893 15.8181 238.154 16.0477 238.404 16.29H238.731C241.079 17.6138 243.922 18.29 246.987 18.29C252.062 18.29 256.531 16.2393 259.143 12.5117C259.932 12.3666 260.742 12.2901 261.569 12.29C263.093 12.29 264.562 12.5476 265.942 13.0234C268.57 16.4953 272.979 18.29 277.975 18.29C282.751 18.29 286.991 16.4737 289.65 13.1523C291.134 12.5944 292.724 12.29 294.38 12.29C296.413 12.2901 298.348 12.7477 300.105 13.5723C302.77 16.6844 306.971 18.29 311.696 18.29C316.657 18.29 321.038 16.3303 323.672 12.7607C324.799 12.4544 325.976 12.2901 327.189 12.29C330.109 12.29 332.826 13.2326 335.106 14.8535C335.41 15.1146 335.727 15.361 336.056 15.5928C336.322 15.8166 336.583 16.047 336.834 16.29H337.162C339.509 17.6137 342.353 18.29 345.418 18.29Z"
                fill={waveColor}
            />
        </svg>
    )
}

// App Store + Google Play badges linking to the brand's respective store URLs.
const StoreBadges: React.FC<{ android: string; ios: string }> = ({ android, ios }) => (
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
        <MuiLink href={android} target="_blank" rel="noopener noreferrer">
            <Box
                component="img"
                src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                alt="Get it on Google Play"
                sx={{ height: 40, borderRadius: 1 }}
            />
        </MuiLink>
        <MuiLink href={ios} target="_blank" rel="noopener noreferrer">
            <Box
                component="img"
                src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                alt="Download on the App Store"
                sx={{ height: 40, borderRadius: 1 }}
            />
        </MuiLink>
    </Box>
)

interface FooterLink {
    label: string
    href: string
    target?: string
}

interface SocialMediaLink {
    name: string
    href: string
    icon: SvgIconComponent | string
}

interface HomepageFooterProps {
    locale: string
    companyLinks: FooterLink[]
    supportLinks: FooterLink[]
    socialMediaLinks: SocialMediaLink[]
    isSignedIn?: boolean
    uspData: any
    topAirlines?: Airline[]
    hostname?: string
    showHomeHighlights?: boolean
}

const HomepageFooter: React.FC<HomepageFooterProps> = ({
    locale,
    companyLinks,
    supportLinks,
    socialMediaLinks,
    isSignedIn = false,
    uspData,
    topAirlines,
    hostname,
    showHomeHighlights = true
}) => {
    const t = useTranslations('footer')
    const tApp = useTranslations('appDownload')
    const tVariants = useTranslations('variants')
    const brandName = tVariants(getBrandName())
    const theme = useTheme()
    const [showTravelLicense, setShowTravelLicense] = useState(false)
    const flyin = isFlyin();
    const { mobileBg, mobileText, mobileTextMuted } = theme.palette.appDownload
    const links = flyin ? FLYIN_APP_LINKS : CLEARTRIP_APP_LINKS
    const supportCardsImageSrc = getSupportCardsImageSrc({ locale, hostname })

    useEffect(() => {
        setShowTravelLicense(isGlobalDomain())
    }, [])

    return (
        <Box sx={{ bgcolor: 'background.default', pb: 7 }}>
            {showHomeHighlights && (
                <HomeHighlights locale={locale} maxWidth="xl" isMobile uspData={uspData} />
            )}

            {/* Top Icons Section */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    pt: 1,
                    pb: 4,
                    px: 2,
                    bgcolor: 'background.paper'
                }}
            >
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                    <Box
                        sx={{
                            width: 56,
                            height: 56,
                            borderRadius: '50%',
                            bgcolor: 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto'
                        }}
                    >
                        <HeadsetMicIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                    </Box>
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'text.secondary',
                            fontSize: '11px',
                            fontWeight: 500,
                            display: 'block',
                            whiteSpace: 'pre-line'
                        }}
                    >
                        {t('customerSupport247').replace(' ', '\n')}
                    </Typography>
                </Box>

                <Box sx={{ textAlign: 'center', flex: 1 }}>
                    <Box
                        sx={{
                            width: 56,
                            height: 56,
                            borderRadius: '50%',
                            bgcolor: 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto'
                        }}
                    >
                        <FlashOnIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                    </Box>
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'text.secondary',
                            fontSize: '11px',
                            fontWeight: 500,
                            display: 'block',
                            whiteSpace: 'pre-line'
                        }}
                    >
                        {t('easyFastBooking').replace(' ', '\n')}
                    </Typography>
                </Box>

                <Box sx={{ textAlign: 'center', flex: 1 }}>
                    <Box
                        sx={{
                            width: 56,
                            height: 56,
                            borderRadius: '50%',
                            bgcolor: 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto'
                        }}
                    >
                        <PaymentIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                    </Box>
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'text.secondary',
                            fontSize: '11px',
                            fontWeight: 500,
                            display: 'block',
                            whiteSpace: 'pre-line'
                        }}
                    >
                        {t('securePaymentOptions').replace(' ', '\n')}
                    </Typography>
                </Box>
            </Box>

            {/* App Download Section with Wave */}
            <FeatureRenderer type="APP_DOWNLOAD_BANNER">
                <Box sx={{ position: 'relative' }}>
                    {/* Top Wave */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '60px',
                            transform: 'translateY(-30%)'
                        }}
                    >
                        <WaveDivider inverted color={mobileBg} />
                    </Box>

                {/* Brand Background Section */}
                <Box
                    sx={{
                        bgcolor: mobileBg,
                        color: mobileText,
                        pt: 5,
                        pb: 4,
                        px: 3,
                        textAlign: 'center'
                    }}
                >
                    <Typography
                        component="h2"
                        variant="h6"
                        sx={{ fontWeight: 700, mb: flyin ? 3 : 1, fontSize: '18px', lineHeight: 1.4 }}
                    >
                        {tApp('mobileTitle', { brandName })}
                    </Typography>

                    {flyin && (
                        <>
                        {/* Stats */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 3, px: 2 }}>
                            <Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 0.5,
                                        mb: 0.5
                                    }}
                                >
                                    <Typography variant="h3" sx={{ fontWeight: 700, fontSize: '20px' }}>
                                        {APP_RATINGS.GOOGLE_PLAY}
                                    </Typography>
                                    <StarIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                                </Box>
                                <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 400 }}>
                                    {tApp('googlePlay')}
                                </Typography>
                            </Box>
                            <Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 0.5,
                                        mb: 0.5
                                    }}
                                >
                                    <Typography variant="h3" sx={{ fontWeight: 700, fontSize: '20px' }}>
                                        {APP_RATINGS.APP_STORE}
                                    </Typography>
                                    <StarIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                                </Box>
                                <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 400 }}>
                                    {tApp('appStore')}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5, fontSize: '20px' }}>
                                    {APP_RATINGS.DOWNLOADS}
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 400 }}>
                                    {tApp('downloads')}
                                </Typography>
                            </Box>
                        </Box>

                        {/* App Store Buttons */}
                        <StoreBadges android={links.android} ios={links.ios} />
                        </>
                    )}
                    {!flyin && (
                        <>
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    mb: 1,
                                    color: mobileTextMuted,
                                    maxWidth: 320,
                                    mx: 'auto'
                                }}
                            >
                                {tApp('subtitle')}
                            </Typography>
                            <Box sx={{ mb: 2 }}>
                                <StoreBadges android={links.android} ios={links.ios} />
                            </Box>
                            <Typography sx={{ fontSize: '10px', fontWeight: 400, color: mobileTextMuted }}>
                                {tApp('availability')}
                            </Typography>
                        </>
                    )}
                    </Box>

                    {/* Bottom Wave */}
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: '60px',
                            transform: 'translateY(70%)'
                        }}
                    >
                        <WaveDivider color={mobileBg} />
                    </Box>
                </Box>
            </FeatureRenderer>
            <Box sx={{ bgcolor: 'grey.100', pt: 3, pb: 1 }}>
                {/* Social Media Icons */}
                <FeatureRenderer type="SOCIAL_LINKS">
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 1.5,
                            py: 3,
                            px: 2
                        }}
                    >
                        {socialMediaLinks.map(social => (
                            <IconButton
                                key={social.name}
                                href={social.href}
                                target="_blank"
                                sx={{
                                    bgcolor: 'common.white',
                                    width: 48,
                                    height: 48,
                                    borderRadius: '30%',
                                    '&:hover': { bgcolor: 'grey.100', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }
                                }}
                            >
                                {social.icon === 'twitter' ? (
                                    <TwitterIcon sx={{ color: 'text.primary', fontSize: 20 }} />
                                ) : social.icon === 'threads' ? (
                                    <ThreadsIcon sx={{ color: 'text.primary', fontSize: 20 }} />
                                ) : (
                                    React.createElement(social.icon as SvgIconComponent, {
                                        sx: { color: 'text.primary', fontSize: 24 }
                                    })
                                )}
                            </IconButton>
                        ))}
                    </Box>
                </FeatureRenderer>

                {/* Company and Support Links */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 3,
                        px: 3,
                        py: 2
                    }}
                >
                    {/* Company Column */}
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, fontSize: '16px' }}>
                            {t('company')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {companyLinks.map((link, index) => (
                                <MuiLink
                                    key={index}
                                    href={link.href}
                                    target={link.target}
                                    rel={link.target === '_blank' ? 'noopener noreferrer' : undefined}
                                    sx={{
                                        color: 'text.secondary',
                                        textDecoration: 'none',
                                        fontSize: '14px',
                                        textAlign: 'left',
                                        '&:hover': { color: 'primary.main' }
                                    }}
                                >
                                    {link.label}
                                </MuiLink>
                            ))}
                        </Box>
                    </Box>

                    {/* Support Column */}
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, fontSize: '16px' }}>
                            {t('support')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {supportLinks.map((link, index) => (
                                <MuiLink
                                    key={index}
                                    href={link.href}
                                    target={link.target}
                                    rel={link.target === '_blank' ? 'noopener noreferrer' : undefined}
                                    sx={{
                                        color: 'text.secondary',
                                        textDecoration: 'none',
                                        fontSize: '14px',
                                        textAlign: 'left',
                                        '&:hover': { color: 'primary.main' }
                                    }}
                                >
                                    {link.label}
                                </MuiLink>
                            ))}
                        </Box>
                    </Box>
                </Box>
                <Box sx={{ height: '1px', bgcolor: 'divider' }} />

                {/* Top Destinations Section */}
                <FeatureRenderer type="TOP_DESTINATIONS">
                    <TopDestinationsWrapper />
                    <Box sx={{ height: '1px', bgcolor: 'divider', m: 1 }} />
                </FeatureRenderer>

                {/* Explore Other Links Section */}
                <FeatureRenderer type="EXPLORE_OTHER_LINKS">
                    <ExploreOtherLinksWrapper />
                    <Box sx={{ height: '1px', bgcolor: 'divider', m: 1 }} />
                </FeatureRenderer>

                {/* Top Airlines Section */}
                <FeatureRenderer type="TOP_AIRLINES">
                    <TopAirlinesWrapper topAirlines={topAirlines} />
                    <Box sx={{ height: '1px', bgcolor: 'divider' }} />
                </FeatureRenderer>

                {/* Top Countries Section */}
                <FeatureRenderer type="TOP_COUNTRIES">
                    <TopCountriesWrapper />
                    <Box sx={{ height: '1px', bgcolor: 'divider', m: 1 }} />
                </FeatureRenderer>

                {/* License Information */}
                {flyin && <Box sx={{ bgcolor: 'grey.100' }}>
                    <Box sx={{ px: 3, pt: 2 }}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'text.primary',
                                fontSize: '12px',
                                display: 'block',
                                lineHeight: 1.6
                            }}
                        >
                            {t('commercialRegistrationNumber')}:{' '}
                            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                {t('commercialRegistrationValue')}
                            </Box>
                        </Typography>
                    </Box>

                    {showTravelLicense && (
                        <Box sx={{ px: 3, pt: 1 }}>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'text.primary',
                                    fontSize: '12px',
                                    display: 'block',
                                    lineHeight: 1.6
                                }}
                            >
                                {t('ministryOfTourismLicense')}:{' '}
                                <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                    {t('ministryOfTourismLicenseValue')}
                                </Box>
                            </Typography>
                        </Box>
                    )}

                    <Box sx={{ px: 3, py: showTravelLicense ? 1 : 2, pt: 1 }}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'text.primary',
                                fontSize: '12px',
                                display: 'block',
                                lineHeight: 1.6
                            }}
                        >
                            {t('licenseCategory')}:{' '}
                            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                {t('travelAndTourismAgency')}
                            </Box>
                        </Typography>
                    </Box>
                    <Box sx={{ height: '1px', bgcolor: 'divider' }} />
                </Box>}
                {/* Support Cards (Flyin only) */}
                {flyin && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            px: 3,
                            py: 2
                        }}
                    >
                        <Box
                            component="img"
                            src={supportCardsImageSrc}
                            alt="Payment Methods"
                            sx={{ height: 30, maxWidth: '100%' }}
                        />
                    </Box>
                )}
                <Box sx={{ height: '1px', bgcolor: 'divider' }} />

                <CopyRight/>
            </Box>
        </Box>
    )
}

export { HomepageFooter }
export type { HomepageFooterProps }

